import React, { useState } from "react";
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { API_ROOT } from "@/config";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface ExtractedBriefingData {
  campaign_name: string;
  brand_name: string;
  product_name: string;
  hooks: string[];
  hooks_must_be_present: boolean;
  topics: string[];
  topics_must_be_present: boolean;
  call_to_actions: string[];
  call_to_actions_must_be_present: boolean;
  elements: string[];
  elements_must_be_present: boolean;
  story: string;
  story_must_be_present: boolean;
  visuals_background: string;
  visuals_background_must_be_present: boolean;
  brand_logo_path?: string;
  brand_logo_must_be_present: boolean;
  product_image_path?: string;
  product_image_must_be_present: boolean;
}

interface BriefingUploaderProps {
  onBriefingExtracted: (data: ExtractedBriefingData) => void;
}

const BriefingUploader: React.FC<BriefingUploaderProps> = ({ onBriefingExtracted }) => {
  const [mode, setMode] = useState<'upload' | 'paste'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Please upload a PDF, Word document, or text file');
        setSelectedFile(null);
      }
    }
  };

  const handleExtractFromFile = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);
    setProgress(0);
    setProgressStage('Preparing document...');

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev < 20) {
          setProgressStage('Reading document...');
          return prev + 2;
        } else if (prev < 50) {
          setProgressStage('Extracting text...');
          return prev + 3;
        } else if (prev < 80) {
          setProgressStage('Analyzing content...');
          return prev + 2;
        } else if (prev < 95) {
          setProgressStage('Structuring data...');
          return prev + 1;
        } else {
          setProgressStage('Finalizing...');
          return Math.min(prev + 0.5, 99);
        }
      });
    }, 200);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ROOT}/extract-briefing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(100);
      setProgressStage('Complete!');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(errorData.detail || `Failed to extract briefing data (${response.status})`);
      }

      const data = await response.json();
      // Directly pass to parent - no intermediate success screen
      onBriefingExtracted(data);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Briefing extraction error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to extract briefing';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setProgressStage('');
    }
  };

  const handleExtractFromText = async () => {
    if (!pastedText.trim()) {
      setError('Please paste some text');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProgress(0);
    setProgressStage('Preparing text...');

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev < 30) {
          setProgressStage('Analyzing text...');
          return prev + 3;
        } else if (prev < 70) {
          setProgressStage('Extracting requirements...');
          return prev + 4;
        } else if (prev < 95) {
          setProgressStage('Structuring data...');
          return prev + 2;
        } else {
          setProgressStage('Finalizing...');
          return Math.min(prev + 0.5, 99);
        }
      });
    }, 200);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ROOT}/extract-briefing-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: pastedText }),
      });

      clearInterval(progressInterval);
      setProgress(100);
      setProgressStage('Complete!');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(errorData.detail || `Failed to extract briefing data (${response.status})`);
      }

      const data = await response.json();
      // Directly pass to parent - no intermediate success screen
      onBriefingExtracted(data);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Briefing extraction error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to extract briefing';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setProgressStage('');
    }
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardContent className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold" style={{ color: PALETTE.gunmetal }}>
            Upload Briefing
          </h2>
        </div>

        {!isProcessing && (
          <>
            {/* Subtle Tab System */}
            <div className="flex gap-8 mb-6 border-b" style={{ borderColor: `${PALETTE.americanSilver}40` }}>
              <button
                onClick={() => setMode('upload')}
                className="pb-3 px-1 text-base font-medium transition-colors relative"
                style={{ 
                  color: mode === 'upload' ? PALETTE.gunmetal : `${PALETTE.gunmetal}50`
                }}
              >
                File Upload
                {mode === 'upload' && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ background: PALETTE.inchworm }}
                  />
                )}
              </button>
              <button
                onClick={() => setMode('paste')}
                className="pb-3 px-1 text-base font-medium transition-colors relative"
                style={{ 
                  color: mode === 'paste' ? PALETTE.gunmetal : `${PALETTE.gunmetal}50`
                }}
              >
                Paste Text
                {mode === 'paste' && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ background: PALETTE.inchworm }}
                  />
                )}
              </button>
            </div>

            {/* Unified Smart Card */}
            <div className="space-y-6">
              {mode === 'upload' ? (
                /* File Upload Drop Zone */
                <div
                  className="border-2 border-dashed rounded-lg h-[320px] flex flex-col items-center justify-center text-center cursor-pointer transition-all hover:bg-gray-50/50"
                  style={{ 
                    borderColor: `${PALETTE.americanSilver}80`,
                    background: '#F9F9F9'
                  }}
                  onClick={() => document.getElementById('briefing-file-input')?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = PALETTE.inchworm;
                    e.currentTarget.style.background = `${PALETTE.inchworm}10`;
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.borderColor = `${PALETTE.americanSilver}80`;
                    e.currentTarget.style.background = '#F9F9F9';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = `${PALETTE.americanSilver}80`;
                    e.currentTarget.style.background = '#F9F9F9';
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      const validTypes = [
                        'application/pdf',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'text/plain'
                      ];
                      if (validTypes.includes(file.type)) {
                        setSelectedFile(file);
                        setError(null);
                      } else {
                        setError('Please upload a PDF, Word document, or text file');
                      }
                    }
                  }}
                >
                  <input
                    id="briefing-file-input"
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {/* Icon */}
                  <Upload 
                    className="w-12 h-12 mx-auto mb-4" 
                    style={{ color: `${PALETTE.gunmetal}40`, strokeWidth: 1.5 }} 
                  />
                  
                  {/* Main Text */}
                  <p className="text-lg font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                    {selectedFile ? selectedFile.name : 'Drag and drop your file here'}
                  </p>
                  
                  {/* Browse Link */}
                  <p className="text-base mb-4" style={{ color: `${PALETTE.gunmetal}60` }}>
                    or{' '}
                    <span 
                      className="font-medium hover:underline cursor-pointer"
                      style={{ color: PALETTE.inchworm }}
                    >
                      Browse Files
                    </span>
                  </p>
                  
                  {/* File Info */}
                  <p className="text-sm" style={{ color: `${PALETTE.gunmetal}50` }}>
                    Supports PDF, DOCX, TXT (Max 10MB)
                  </p>
                </div>
              ) : (
                /* Paste Text Area */
                <div
                  className="border-2 border-dashed rounded-lg overflow-hidden h-[320px]"
                  style={{ 
                    borderColor: `${PALETTE.americanSilver}80`,
                    background: '#F9F9F9'
                  }}
                >
                  <Textarea
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder="Paste your campaign requirements here..."
                    className="h-full border-0 font-mono text-sm resize-none focus:outline-none focus:ring-0"
                    style={{ 
                      background: 'transparent',
                      color: PALETTE.gunmetal
                    }}
                  />
                </div>
              )}

              {/* Action Button */}
              {((mode === 'upload' && selectedFile) || (mode === 'paste' && pastedText.trim())) && (
                <div className="flex justify-center pt-2">
                  <button
                    onClick={mode === 'upload' ? handleExtractFromFile : handleExtractFromText}
                    disabled={isProcessing}
                    className="px-8 py-3 rounded-lg font-medium text-base transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ 
                      background: PALETTE.inchworm,
                      color: PALETTE.gunmetal
                    }}
                  >
                    Analyze Briefing →
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Processing Progress Indicator */}
        {isProcessing && (
          <div className="mt-6 flex flex-col items-center justify-center py-8">
            <p className="text-lg font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
              {progressStage || 'Extracting briefing data...'}
            </p>
            {/* Progress bar */}
            <div className="w-full max-w-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                  {Math.round(progress)}%
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: `${PALETTE.americanSilver}20` }}>
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: `${progress}%`,
                    background: PALETTE.inchworm,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && !isProcessing && (
          <div className="mt-4 p-4 rounded-lg flex items-start space-x-3" style={{ background: `${PALETTE.orange}10` }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: PALETTE.orange }} />
            <p className="text-sm" style={{ color: PALETTE.orange }}>{error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BriefingUploader;

